import { useRef } from 'react';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

interface ResizeHandleProps {
  height: number;
  onChange: (height: number) => void;
  onCollapse: () => void;
  min: number;
  max: number;
}

// Drag handle at the top of the fully-expanded composer. Clamped to
// min/max; dragging down to the floor snaps back to the engaged (not fully
// expanded) tier, mirroring the Collapse button.
export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  height,
  onChange,
  onCollapse,
  min,
  max,
}) => {
  const t = useTranslations();
  const drag = useRef<{ startY: number; startHeight: number } | null>(null);
  // Tracks the latest clamped height during a drag — `endDrag` is bound at
  // mousedown time, so it can't read a fresh `height` prop from its closure.
  const lastHeight = useRef(height);

  const clamp = (value: number) => Math.min(max, Math.max(min, value));

  const handleMouseMove = (event: MouseEvent) => {
    if (!drag.current) return;
    const delta = drag.current.startY - event.clientY;
    const next = clamp(drag.current.startHeight + delta);
    lastHeight.current = next;
    onChange(next);
  };

  const endDrag = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', endDrag);
    drag.current = null;

    if (lastHeight.current <= min) onCollapse();
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    drag.current = { startY: event.clientY, startHeight: height };
    lastHeight.current = height;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endDrag);
  };

  return (
    <div
      className={styles.hitArea}
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="horizontal"
      aria-label={t('memoComposer.resize')}
    >
      <div className={styles.grip} />
    </div>
  );
};

const styles = {
  hitArea: classNames(
    'flex items-center justify-center h-[18px] cursor-row-resize select-none',
  ),
  grip: 'w-10 h-1 rounded-full bg-ink-secondary/30',
};
